import { useState, useCallback, useEffect, Key } from 'react';

import EditableTable from './EditableTable/EditableTable';

import { CURRENCY_PRECISION } from '../constants';
import { InputTypeEnum, IDataProps, IConfigurationType } from '../types';
import API from '../../../../utils/api';
import { ClientService } from '../../../../shared/api/ClientService';
import genericMessage from '../../../../utils/genericMessage';
import useLocale from '../../../../hooks/useLocale';

interface IProps {
  isEditable?: boolean;
  setIsEditable?: (isEditable: boolean) => void;
}

function OSBOtherFeesTable({ isEditable, setIsEditable }: IProps): JSX.Element {
  const { t } = useLocale();

  const [data, setData] = useState<IDataProps<IConfigurationType>[]>();
  const [loading, setLoading] = useState<boolean>(false);

  const requestConfigurations = useCallback(async () => {
    setLoading(true);
    const response = await API.getFileAnalysisOSBFilingAndOtherFeeConfigurationsList().catch(() => setLoading(false));
    if (response) setData(response?.map((item) => ({ ...item, key: item.id as Key, hasError: false })));
    setLoading(false);
  }, []);

  const requestUpdateConfigurations1 = useCallback(
    async (data?: IDataProps<IConfigurationType>[]) => {
      const body = data?.map((item: any) => ({
        id: item?.id,
        amount: Number(item?.amount),
      })) as ClientService.FileAnalysisOSBFilingAndOtherFeeConfigurationInputDto[];
      setLoading(true);
      const response = await API.updateFileAnalysisOSBFilingAndOtherFeeConfigurations(body).catch(() =>
        setLoading(false)
      );
      if (response?.result === ClientService.Result.Successful) {
        genericMessage.success(t.CONFIGURATION_CHANGE_SUCCESS);
        if (setIsEditable) setIsEditable(false);
        requestConfigurations();
      } else {
        genericMessage.error({}, response?.messages?.[0].body);
      }
      setLoading(false);
    },
    [requestConfigurations, setIsEditable, t.CONFIGURATION_CHANGE_SUCCESS]
  );

  const handleCancel = useCallback(() => {
    if (setIsEditable) setIsEditable(false);
    requestConfigurations();
  }, [requestConfigurations, setIsEditable]);

  useEffect(() => {
    if (!data) requestConfigurations();
  }, [data, requestConfigurations]);

  const defaultColumns = [
    {
      title: t.FEE_TYPE,
      dataIndex: 'feeType',
      key: 'feeType',
      type: InputTypeEnum.ReadOnly,
      width: '500px',
    },
    {
      title: t.AMOUNT,
      dataIndex: 'amount',
      key: 'amount',
      type: InputTypeEnum.Currency,
      precision: CURRENCY_PRECISION,
    },
  ];

  return (
    <EditableTable
      data={data}
      loading={loading}
      defaultColumns={defaultColumns}
      isEditable={isEditable}
      onSave={requestUpdateConfigurations1}
      onCancel={handleCancel}
    />
  );
}

export default OSBOtherFeesTable;
